import { getStoreInfo } from './api';
import i18n from '../i18n';
import type { Category, CartItem } from '../types';

/**
 * v5 — HAPPY HOUR (remise automatique sur une catégorie à certaines heures).
 *
 * 2 endpoints :
 *  1. get_active_happy_hour : détecter si un happy hour tourne (polling 5 min)
 *  2. apply_happy_hour      : déclarer la remise AVANT pay_order_payment
 *
 * ⚠️ LE SERVEUR FAIT AUTORITÉ : les prix affichés côté kiosk sont INDICATIFS.
 * Le montant officiel est celui renvoyé par apply_happy_hour.
 *
 * ⚠️ NF525 : la remise doit apparaître sur le ticket (nom + % + montant).
 */

const API_BASE = 'https://api.restomi.fr/v3/api/store';

export interface HappyHour {
  _id: string;
  name: string;
  /** ID de la CATÉGORIE concernée. null = tout le menu. */
  product_group_id: string | null;
  discount_percent: number;
  /** Minutes depuis minuit (ex: 1020 = 17h00) */
  start_time: number;
  /** Minutes depuis minuit (ex: 1140 = 19h00) */
  end_time: number;
}

export interface ActiveHappyHourResponse {
  success: boolean;
  active: boolean;
  happy_hour: HappyHour | null;
}

export interface ApplyHappyHourResult {
  success: boolean;
  applied: boolean;
  happy_hour_name?: string;
  happy_hour_percent?: number;
  eligible_amount?: number;
  /** Montant OFFICIEL de la remise (fait foi) */
  happy_hour_discount: number;
  message?: string;
}

function buildHeaders(): HeadersInit | null {
  const storeInfo = getStoreInfo();
  if (!storeInfo) return null;
  return {
    'Content-Type': 'application/json',
    storeid: storeInfo.store_id,
    token: storeInfo.server_token,
    type: '0',
    lang: i18n.language || 'fr',
  };
}

/**
 * Détecte le happy hour actif pour le canal kiosk.
 *
 * En cas d'erreur → retourne null. L'appelant traite null comme "pas de happy hour"
 * (on n'invente jamais une remise à cause d'un souci réseau).
 */
export async function fetchActiveHappyHour(): Promise<HappyHour | null> {
  try {
    const headers = buildHeaders();
    if (!headers) {
      console.warn('[HappyHour] Store info non disponible');
      return null;
    }
    const response = await fetch(`${API_BASE}/get_active_happy_hour`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ channel: 'kiosk' }),
    });
    if (!response.ok) {
      console.warn('[HappyHour] get_active_happy_hour HTTP', response.status);
      return null;
    }
    const data: ActiveHappyHourResponse = await response.json();
    if (!data.success || !data.active || !data.happy_hour) {
      console.log('[HappyHour] Aucun happy hour actif');
      return null;
    }
    console.log('[HappyHour] Happy hour actif ←', data.happy_hour);
    return data.happy_hour;
  } catch (e) {
    console.error('[HappyHour] fetchActiveHappyHour error', e);
    return null;
  }
}

/**
 * Déclare la remise happy hour sur l'order_payment.
 * À appeler APRÈS get_order_cart_invoice et AVANT apply_loyalty_discount / pay_order_payment.
 *
 * @param orderPaymentId order_payment en cours
 * @param eligibleAmount Somme des prix NORMAUX (avant remise) des articles éligibles
 */
export async function applyHappyHour(
  orderPaymentId: string,
  eligibleAmount: number
): Promise<ApplyHappyHourResult> {
  try {
    const headers = buildHeaders();
    if (!headers) {
      return { success: false, applied: false, happy_hour_discount: 0, message: 'Store info non disponible' };
    }
    const body = {
      order_payment_id: orderPaymentId,
      channel: 'kiosk',
      eligible_amount: eligibleAmount,
    };
    console.log('[HappyHour] apply_happy_hour →', body);
    const response = await fetch(`${API_BASE}/apply_happy_hour`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const txt = await response.text();
      console.warn('[HappyHour] apply_happy_hour HTTP', response.status, txt);
      return { success: false, applied: false, happy_hour_discount: 0, message: `HTTP ${response.status}` };
    }
    const data: ApplyHappyHourResult = await response.json();
    console.log('[HappyHour] apply_happy_hour ←', data);
    return {
      ...data,
      happy_hour_discount: Number(data.happy_hour_discount) || 0,
    };
  } catch (e) {
    console.error('[HappyHour] applyHappyHour error', e);
    return { success: false, applied: false, happy_hour_discount: 0, message: 'Network error' };
  }
}

/* ============================================================
 * Helpers
 * ============================================================ */

/**
 * Convertit des minutes depuis minuit en libellé horaire.
 * 1140 → "19h00"
 */
export function formatHappyHourTime(minutesSinceMidnight: number): string {
  const total = Math.max(0, Math.floor(minutesSinceMidnight));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h${String(m).padStart(2, '0')}`;
}

/**
 * Prix remisé pour affichage (INDICATIF — le serveur fait autorité).
 */
export function getDiscountedPrice(price: number, discountPercent: number): number {
  return Math.round(price * (1 - discountPercent / 100) * 100) / 100;
}

/**
 * Set des product_ids éligibles au happy hour.
 * - product_group_id null → null (= TOUS les produits éligibles)
 * - sinon → les product_ids de la catégorie ciblée
 *
 * Côté kiosk, menuItem.id === product._id (voir App.tsx construction du MenuItem),
 * donc on peut tester l'appartenance directement.
 */
export function getEligibleProductIds(
  happyHour: HappyHour | null,
  categories: Category[]
): Set<string> | null {
  if (!happyHour) return new Set();
  // null = tout le menu
  if (!happyHour.product_group_id) return null;

  const category = categories.find((c) => c._id === happyHour.product_group_id);
  if (!category || !category.product_ids?.length) {
    console.warn(
      '[HappyHour] Catégorie ciblée introuvable ou vide:',
      happyHour.product_group_id
    );
    return new Set();
  }
  return new Set(category.product_ids);
}

/**
 * Un produit est-il éligible au happy hour ?
 * @param eligibleIds null = tout le menu éligible
 */
export function isProductEligible(
  productId: string,
  eligibleIds: Set<string> | null
): boolean {
  if (eligibleIds === null) return true; // tout le menu
  return eligibleIds.has(productId);
}

/**
 * Nom de la catégorie ciblée, pour le bandeau.
 * Retourne null si product_group_id est null (= tout le menu).
 */
export function getTargetCategoryName(
  happyHour: HappyHour | null,
  categories: Category[]
): string | null {
  if (!happyHour || !happyHour.product_group_id) return null;
  const category = categories.find((c) => c._id === happyHour.product_group_id);
  if (!category) return null;
  return Array.isArray(category.name) ? category.name[0] : (category.name as any);
}

/**
 * Calcule eligible_amount = somme des prix NORMAUX (avant remise) des articles
 * du panier appartenant à la catégorie ciblée.
 *
 * Inclut les suppléments (customizations) car ils font partie du prix payé,
 * et le total du panier est calculé de la même façon (base + options) × quantité.
 *
 * @param eligibleIds null = tout le menu → on somme tout le panier
 */
export function computeEligibleAmount(
  items: CartItem[],
  eligibleIds: Set<string> | null
): number {
  const sum = items.reduce((acc, item) => {
    if (!isProductEligible(item.menuItem.id, eligibleIds)) return acc;
    const basePrice = item.menuItem.price;
    const optionsPrice = Object.values(item.customizations || {}).reduce(
      (s, options) => s + options.reduce((os, opt) => os + (opt.price || 0), 0),
      0
    );
    return acc + (basePrice + optionsPrice) * item.quantity;
  }, 0);
  // Arrondi 2 décimales pour éviter les flottants type 12.499999999
  return Math.round(sum * 100) / 100;
}
